import React, { useEffect, useState, useRef } from 'react';
import { useLocalizedNavigate } from '../hooks/useLocalizedNavigate';
import { serviceAPI } from '../services/api';
import { useI18n } from '../i18n/i18nContext';
import { getTranslatedField } from '../utils/translationUtils';
import { useUser } from '../contexts/UserContext';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import { useNoIndex } from '../hooks/useNoIndex';
import { getCachedData, setCachedData } from '../utils/cache';
import { useServiceMetaStore } from '../stores/serviceMetaStore';
import { Package, Service } from '@shared/types';
import {
  pushToDataLayer,
  clearEcommerce,
  formatServiceItem,
  formatPackageItem,
} from '../utils/dataLayer';
import SEO from '../components/SEO';
import { usePackageStore } from 'src/stores/packageStore';

const BookingPage: React.FC = () => {
  const navigate = useLocalizedNavigate();
  const { t, language } = useI18n();
  useDocumentTitle(t.pageTitles.book);
  useNoIndex();
  const { isLoadingUser } = useUser();
  const { setMeta } = useServiceMetaStore();
  const { packages, loadPackages } = usePackageStore();

  const [services, setServices] = useState<Service[]>(
    () => getCachedData<Service[]>('services') || []
  );
  const [loading, setLoading] = useState(() => !getCachedData<Service[]>('services'));
  const [error, setError] = useState<string | null>(null);
  const hasFetched = useRef(false);
  const hasTrackedViewItemList = useRef(false);

  useEffect(() => {
    loadPackages();
  }, [loadPackages]);

  useEffect(() => {
    if (!hasFetched.current) {
      hasFetched.current = true;
      fetchServices();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (services.length > 0 && packages.length > 0 && !loading && !hasTrackedViewItemList.current) {
      hasTrackedViewItemList.current = true;
      clearEcommerce();
      pushToDataLayer('view_item_list', {
        content: { category: 'booking_service' },
        ecommerce: {
          items: [
            ...services.map((service) => formatServiceItem(service)),
            ...packages.map((pkg) => formatPackageItem(pkg)),
          ],
        },
      });
    }
  }, [services, packages, loading]);

  const fetchServices = async () => {
    const cachedServices = getCachedData<Service[]>('services');

    try {
      const response = await serviceAPI.getAll();
      const activeServices = response.data.filter(
        (service: any) => service.metadata?.is_active === true
      );

      // Store metadata for each service
      activeServices.forEach((service: any) => {
        if (service.maxParticipants) {
          setMeta(service.id, { maxParticipants: service.maxParticipants });
        }
      });

      if (JSON.stringify(activeServices) !== JSON.stringify(cachedServices)) {
        setServices(activeServices);
        setCachedData('services', activeServices);
      }
      setLoading(false);
    } catch (err) {
      console.error(t.console.errors.fetchingServices, err);
      if (!cachedServices) {
        setError(t.booking.loadError);
      }
      setLoading(false);
    }
  };

  if (loading || isLoadingUser) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="text-gray-500">{t.booking.loadingServices}</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="py-10 text-center">
        <div className="text-red-600">{error}</div>
      </div>
    );
  }

  return (
    <div className="py-10">
      <SEO description={t.seo.bookDescription} />
      <div className="mx-auto max-w-5xl px-4 pb-12 sm:px-6 lg:px-8">
        <div className="mb-8">
          <h1 className="mb-2 text-3xl font-bold text-gray-900">{t.booking.titlePackage}</h1>
          <p className="text-lg text-gray-600">{t.booking.step1package}</p>
        </div>

        <div className="grid gap-6 md:grid-cols-2">
          {packages.map((pkg: Package) => (
            <div
              key={pkg.id}
              className="transform cursor-pointer overflow-hidden rounded-lg bg-white shadow-md transition-transform hover:scale-105"
              onClick={() => navigate(`/book/package/${pkg.id}`)}
            >
              {pkg.image_url ? (
                <img
                  src={pkg.image_url}
                  alt={getTranslatedField(pkg, 'name', language)}
                  className="h-48 w-full object-cover"
                />
              ) : (
                <div className="flex h-48 items-center justify-center bg-gray-200">
                  <span className="text-gray-500">{getTranslatedField(pkg, 'name', language)}</span>
                </div>
              )}
              <div className="p-6">
                <h3 className="mb-2 text-xl font-semibold text-gray-900">
                  {getTranslatedField(pkg, 'name', language)}
                </h3>
                <p className="mb-4 text-gray-600">
                  {getTranslatedField(pkg, 'description', language)}
                </p>
                {getTranslatedField(pkg, 'additional_info', language) && (
                  <div
                    className="mb-4 text-gray-600"
                    dangerouslySetInnerHTML={{
                      __html: getTranslatedField(pkg, 'additional_info', language) || '',
                    }}
                  />
                )}
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-500">
                    {pkg.duration_minutes} {t.booking.minutes}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
      <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
        <div className="mb-8">
          <h1 className="mb-2 text-3xl font-bold text-gray-900">{t.booking.title}</h1>
          <p className="text-lg text-gray-600">{t.booking.step1}</p>
        </div>

        <div className="grid gap-6 md:grid-cols-2">
          {services.map((service) => (
            <div
              key={service.id}
              className="transform cursor-pointer overflow-hidden rounded-lg bg-white shadow-md transition-transform hover:scale-105"
              onClick={() => navigate(`/book/${service.id}`)}
            >
              {service.image ? (
                <img
                  src={service.image}
                  alt={getTranslatedField(service, 'name', language)}
                  className="h-48 w-full object-cover"
                />
              ) : (
                <div className="flex h-48 items-center justify-center bg-gray-200">
                  <span className="text-gray-500">
                    {getTranslatedField(service, 'name', language)}
                  </span>
                </div>
              )}
              <div className="p-6">
                <h3 className="mb-2 text-xl font-semibold text-gray-900">
                  {getTranslatedField(service, 'name', language)}
                </h3>
                <p className="mb-4 text-gray-600">
                  {getTranslatedField(service, 'description', language)}
                </p>
                {getTranslatedField(service, 'additional_info', language) && (
                  <div
                    className="mb-4 text-gray-600"
                    dangerouslySetInnerHTML={{
                      __html: getTranslatedField(service, 'additional_info', language) || '',
                    }}
                  />
                )}
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-500">
                    {service.duration} {t.booking.minutes}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default BookingPage;
